import type { SyntheticEvent } from 'react'
import { useCallback } from 'react'

import { useUnit } from 'effector-react'

import { Button } from '@/components/ui/button.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import type { DialogProps } from '@/dialogs/types'
import { openDialog } from '@/models/dialogs.ts'
import {
  $canLoadMoreVaultCategoryPages,
  $selectedCategory,
  $vaultCategories,
  $vaultCategoriesError,
  getNextVaultCategoriesPageFx,
} from '@/models/vault/categories.ts'
import {
  $mediaHasChanged,
  $mediaToCategoryMap,
  $selectedMediaIds,
  mediaMoveInFx,
  mediaMoveOutFx,
  refetchVaultMedias,
  resetSelectedMedia,
} from '@/models/vault/medias'
import { classNames } from '@/utils/helpers.ts'

import { VaultCategoriesSkeleton } from '../../modules/vault/ui/category/vault-categories-skeleton.tsx'
import { VaultCategoriesSort } from '../../modules/vault/ui/category/vault-categories-sort.tsx'
import { VaultCategory } from '../../modules/vault/ui/category/vault-category.tsx'

interface Props extends DialogProps {}

export const MoveMediaDialog = ({ isOpen, onClose }: Props) => {
  const selectedMediaIds = useUnit($selectedMediaIds)
  const error = useUnit($vaultCategoriesError)
  const mediaToCategoryMap = useUnit($mediaToCategoryMap)
  const isMovingIn = useUnit(mediaMoveInFx.pending)
  const isMovingOut = useUnit(mediaMoveOutFx.pending)
  const hasChanged = useUnit($mediaHasChanged)
  const selectedCategory = useUnit($selectedCategory)
  const disabled = isMovingIn || isMovingOut

  const vaultCategories = useUnit($vaultCategories)
  const isCategoriesLoading = useUnit(getNextVaultCategoriesPageFx.pending)
  const canLoadMore = useUnit($canLoadMoreVaultCategoryPages)

  const handleScroll = (event: SyntheticEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement
    const distanceToEnd =
      target.scrollHeight - target.clientHeight - target.scrollTop

    // fetch next items when only one item left
    if (distanceToEnd < 120 && !isCategoriesLoading && canLoadMore && !error) {
      getNextVaultCategoriesPageFx()
    }
  }

  const handleCreateCategoryClick = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.VAULT_ADD_CATEGORY,
    })
  }, [])

  const getCheckboxState = useCallback(
    (categoryId: number) => {
      const allSelected = selectedMediaIds.every((id) =>
        mediaToCategoryMap[id]?.includes(categoryId),
      )

      if (allSelected) {
        return true
      }

      const nothingSelected = !selectedMediaIds.some((id) =>
        mediaToCategoryMap[id]?.includes(categoryId),
      )

      if (nothingSelected) {
        return false
      }

      return 'indeterminate'
    },
    [mediaToCategoryMap, selectedMediaIds],
  )

  const handleClose = useCallback(() => {
    // refetch category only if it is a custom category
    if (selectedCategory?.type === 'custom' && hasChanged) {
      refetchVaultMedias()
    }
    resetSelectedMedia()
    onClose()
  }, [hasChanged, onClose, selectedCategory?.type])

  return (
    <Dialog
      show={isOpen}
      panelClassName=" mt-4 sm:mt-4 mb-4 sm:mb-4"
      onClose={disabled ? undefined : handleClose}
    >
      <div className="flex h-full w-full flex-col">
        <Dialog.Header className="flex w-full flex-row items-center justify-between">
          <Dialog.Title>Add to Category</Dialog.Title>
          <VaultCategoriesSort showSort={false} />
        </Dialog.Header>

        <div className="my-4">
          <Separator />
        </div>

        <div
          onScroll={handleScroll}
          className="max-h-[320px] overflow-x-hidden overflow-y-scroll"
        >
          {vaultCategories
            .filter(({ type }) => type === 'custom')
            .map((category) => {
              const selected = getCheckboxState(category.id)
              const handleClick = () => {
                // eslint-disable-next-line no-unused-expressions
                selected
                  ? mediaMoveOutFx(category.id)
                  : mediaMoveInFx(category.id)
              }

              return (
                <div
                  key={category.id}
                  className={classNames('flex items-center', {
                    'pointer-events-none': disabled,
                  })}
                  onClick={handleClick}
                >
                  <Checkbox
                    checked={selected}
                    disabled={disabled}
                    className="mr-2 h-5 w-5 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <VaultCategory
                    className="w-full"
                    noSelection
                    category={category}
                  />
                </div>
              )
            })}
          {isCategoriesLoading && (
            <VaultCategoriesSkeleton itemClassName="dark:bg-secondary-500" />
          )}
        </div>

        <Dialog.Footer className="grid grid-cols-2 gap-2">
          <Button onClick={handleCreateCategoryClick}>New Category</Button>
          <Button
            variant="outline"
            isLoading={disabled}
            disabled={disabled}
            onClick={handleClose}
          >
            Close
          </Button>
        </Dialog.Footer>
      </div>
    </Dialog>
  )
}
